import type { InterfaceSchema, LiteralSchema, Schema } from "common/data/schema";
import { DocSchemaLibrary } from "schemas-common/docSchema";

export interface BranchFieldInfo {
  branchSchema: Schema;
  fields: Record<string, Schema>;
}

export function resolveIdentifier(schema: Schema): Schema {
  if (schema.kind === "IDENTIFIER" && DocSchemaLibrary[(schema as any).name]) {
    return DocSchemaLibrary[(schema as any).name];
  }
  return schema;
}

function resolveToConcreteSchema(schema: Schema): Schema {
  let resolved = schema;
  if (resolved.kind === "IDENTIFIER" && DocSchemaLibrary[(resolved as any).name]) {
    resolved = DocSchemaLibrary[(resolved as any).name];
  }
  if (resolved.kind === "TYPE_REFERENCE") {
    resolved = (resolved as any).targetType as Schema;
  }
  return resolved;
}

function collectBranchFields(unionTypes: Schema[]): BranchFieldInfo[] | undefined {
  const result: BranchFieldInfo[] = [];

  for (const branch of unionTypes) {
    const concrete = resolveToConcreteSchema(branch);

    if (concrete.kind === "INTERFACE") {
      result.push({ branchSchema: concrete, fields: (concrete as InterfaceSchema).fields });
      continue;
    }

    if (concrete.kind === "INTERSECTION") {
      const members = (concrete as any).intersectionTypes as Schema[];
      const mergedFields: Record<string, Schema> = {};

      for (const member of members) {
        const memberResolved = resolveToConcreteSchema(resolveIdentifier(member));
        if (memberResolved.kind === "INTERFACE") {
          Object.assign(mergedFields, (memberResolved as InterfaceSchema).fields);
        }
      }

      if (Object.keys(mergedFields).length > 0) {
        result.push({ branchSchema: concrete, fields: mergedFields });
        continue;
      }
    }

    return undefined;
  }

  return result;
}

/**
 * Cache keyed by the schema's `unionTypes` array identity. Schema objects are
 * stable for the process lifetime, so this avoids rebuilding branch field maps
 * on every resolveSchemaForEntity / nested-group collection call.
 */
const discriminatorCache = new WeakMap<Schema[], { field: string; branches: BranchFieldInfo[] } | null>();

export function detectDiscriminator(unionTypes: Schema[]): { field: string; branches: BranchFieldInfo[] } | undefined {
  const cached = discriminatorCache.get(unionTypes);
  if (cached !== undefined) {
    return cached ?? undefined;
  }

  const branches = collectBranchFields(unionTypes);
  if (!branches || branches.length !== unionTypes.length) {
    discriminatorCache.set(unionTypes, null);
    return undefined;
  }

  const firstFieldNames = Object.keys(branches[0].fields);
  for (const field of firstFieldNames) {
    if (branches[0].fields[field]?.kind !== "LITERAL") {
      continue;
    }

    const allHaveLiteral = branches.every((b) => b.fields[field]?.kind === "LITERAL");
    if (!allHaveLiteral) {
      continue;
    }

    const values = branches.map((b) => (b.fields[field] as LiteralSchema).value);
    if (new Set(values).size === values.length) {
      const result = { field, branches };
      discriminatorCache.set(unionTypes, result);
      return result;
    }
  }

  discriminatorCache.set(unionTypes, null);
  return undefined;
}

function unionContainsStringOrAny(unionTypes: Schema[]): boolean {
  for (const member of unionTypes) {
    const resolved = resolveIdentifier(member);
    if (resolved.kind === "STRING" || resolved.kind === "ANY") {
      return true;
    }
    if (resolved.kind === "UNION") {
      const innerTypes = (resolved as any).unionTypes as Schema[];
      if (unionContainsStringOrAny(innerTypes)) {
        return true;
      }
    }
  }
  return false;
}

export function collectUnionLiterals(unionTypes: Schema[]): ReadonlySet<string | number | boolean> | undefined {
  const literals = new Set<string | number | boolean>();
  for (const member of unionTypes) {
    const resolved = resolveIdentifier(member);
    if (resolved.kind === "LITERAL") {
      literals.add((resolved as LiteralSchema).value);
    }
  }
  return literals.size > 0 ? literals : undefined;
}

function isExclusivelyFkTypes(schema: Schema): boolean {
  const resolved = resolveIdentifier(schema);
  if (resolved.kind === "FOREIGN_KEY" || resolved.kind === "FOREIGN_KEY_TABLE") {
    return true;
  }
  if (resolved.kind === "UNION") {
    const innerTypes = (resolved as any).unionTypes as Schema[];
    return innerTypes.every((member: Schema) => isExclusivelyFkTypes(member));
  }
  return false;
}

export function filterUnionMembers(unionTypes: Schema[]): Schema[] {
  if (!unionContainsStringOrAny(unionTypes)) {
    return unionTypes;
  }
  return unionTypes.filter((member) => !isExclusivelyFkTypes(member));
}
